/**
 * ACIL Intelligence - Main Application
 * Plataforma de Inteligencia Operativa Retail - ACIL MEXICO
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Shield } from 'lucide-react';
import './App.css';

// ── Páginas de autenticación — carga inmediata (pequeñas, siempre necesarias)
import Login          from './pages/Login';
import Setup2FA       from './pages/Setup2FA';
import Verify2FA      from './pages/Verify2FA';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import MainLayout     from './components/layout/MainLayout';
import Error403       from './pages/errors/Error403';
import Error404       from './pages/errors/Error404';
import Error500       from './pages/errors/Error500';
import api            from './utils/api';
import { initNativePush } from './utils/nativePush';

// ── Páginas operativas — carga diferida (solo se descarga al navegar a ellas)
const Dashboard              = lazy(() => import('./pages/Dashboard'));
const AperturasCierres       = lazy(() => import('./pages/AperturasCierres'));
const ControlPersonal        = lazy(() => import('./pages/ControlPersonal'));
const InteligenciaOperativa  = lazy(() => import('./pages/InteligenciaOperativa'));
const EstimacionesComerciales = lazy(() => import('./pages/EstimacionesComerciales'));
const ConsumoEnergia         = lazy(() => import('./pages/ConsumoEnergia'));
const TorreControl           = lazy(() => import('./pages/TorreControl'));
const AnaliticaComportamiento = lazy(() => import('./pages/AnaliticaComportamiento'));
const ConteoPersonas         = lazy(() => import('./pages/ConteoPersonas'));
const Temperatura            = lazy(() => import('./pages/Temperatura'));
const SeguridadIncidencias   = lazy(() => import('./pages/SeguridadIncidencias'));
const MapasCalor             = lazy(() => import('./pages/MapasCalor'));
const MapaDetalle            = lazy(() => import('./pages/MapaDetalle'));
const Incidencias            = lazy(() => import('./pages/Incidencias'));
const Alertas                = lazy(() => import('./pages/Alertas'));
const ReglasIncidencias      = lazy(() => import('./pages/ReglasIncidencias'));
const Reportes               = lazy(() => import('./pages/Reportes'));
const CitasMedicas           = lazy(() => import('./pages/CitasMedicas'));
const Usuarios               = lazy(() => import('./pages/Usuarios'));
const AuditLogs              = lazy(() => import('./pages/AuditLogs'));
const Configuracion          = lazy(() => import('./pages/Configuracion'));
const Perfil                 = lazy(() => import('./pages/Perfil'));
const Sucursales             = lazy(() => import('./pages/Sucursales'));
const Regiones                = lazy(() => import('./pages/Regiones'));
const SucursalDetalle        = lazy(() => import('./pages/SucursalDetalle'));
const Empleados              = lazy(() => import('./pages/Empleados'));
const Dispositivos           = lazy(() => import('./pages/Dispositivos'));
const Fuentes                = lazy(() => import('./pages/Fuentes'));
const Clientes               = lazy(() => import('./pages/Clientes'));
const ClienteDetalle         = lazy(() => import('./pages/ClienteDetalle'));
const Cuentas                = lazy(() => import('./pages/Cuentas'));
const CuentaDetalle          = lazy(() => import('./pages/CuentaDetalle'));
const CatalogoEventos        = lazy(() => import('./pages/CatalogoEventos'));
const Notificaciones         = lazy(() => import('./pages/Notificaciones'));
const PoliticaNotificaciones = lazy(() => import('./pages/PoliticaNotificaciones'));
const HigieneCumplimiento     = lazy(() => import('./pages/HigieneCumplimiento'));


function LoadingScreen() {
  return (
    <div className="acil-loading-screen" data-testid="loading-screen">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, background: 'var(--orange)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          <Shield size={22} />
        </div>
        <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--dark)', letterSpacing: '-0.3px' }}>ACIL Intelligence</span>
      </div>
      <div className="acil-spinner acil-spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (user && !loading) initNativePush().catch(err => console.error('[ACIL Push]', err));
  }, [user, loading]);
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return <Outlet />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role?.level < 4) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user.role?.level < 3) return <Navigate to="/403" replace />;
  return children;
}

function PermissionRoute({ permission, children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role?.level >= 4) return children;
  if (!(user.permissions || []).includes(permission)) return <Navigate to="/403" replace />;
  return children;
}

function ClientModuleRoute({ moduleKey, children }) {
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  const level = user?.role?.level || 0;
  const clientId = user?.client_id;

  useEffect(() => {
    if (loading) return;

    if (!user) {
      setAllowed(false);
      setChecking(false);
      return;
    }

    if (level >= 4) {
      setAllowed(true);
      setChecking(false);
      return;
    }

    const ids = clientId
      ? [clientId]
      : (user?.accessible_clients || []).map(c => c.id).filter(Boolean);
    if (ids.length === 0) {
      setAllowed(false);
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    setAllowed(false);

    Promise.all(ids.map(id => api.get(`/api/client-modules/${id}`)))
      .then(results => {
        if (!cancelled) {
          const moduleSets = results.map(({ data }) => data?.modules || {});
          // Con un cliente activo se respeta su configuración. En vista global,
          // el módulo solo se abre si está habilitado en todos los clientes
          // accesibles, evitando mostrar datos de contratos deshabilitados.
          setAllowed(
            clientId
              ? moduleSets[0]?.[moduleKey] === true
              : moduleSets.length > 0 && moduleSets.every(mods => mods?.[moduleKey] === true)
          );
          setChecking(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAllowed(false);
          setChecking(false);
        }
      });

    return () => { cancelled = true; };
  }, [clientId, level, loading, moduleKey, user]);

  if (loading || checking) return <LoadingScreen />;
  if (!allowed) return <Navigate to="/403" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public auth routes */}
        <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/auth/setup-2fa" element={<Setup2FA />} />
        <Route path="/auth/verify-2fa" element={<Verify2FA />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />

        {/* Protected routes with layout */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/dashboard"               element={<PermissionRoute permission="ver_dashboard"><Dashboard /></PermissionRoute>} />
            <Route path="/aperturas-cierres"       element={<PermissionRoute permission="ver_apertura_cierre"><AperturasCierres /></PermissionRoute>} />
            <Route path="/control-personal"        element={<PermissionRoute permission="ver_asistencia"><ControlPersonal /></PermissionRoute>} />
            <Route
              path="/torre-control"
              element={
                <PermissionRoute permission="ver_torre_control">
                  <ClientModuleRoute moduleKey="torre_control">
                    <TorreControl />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route path="/inteligencia-operativa"  element={<PermissionRoute permission="ver_trafico"><InteligenciaOperativa /></PermissionRoute>} />
            <Route
              path="/analitica-comportamiento"
              element={
                <PermissionRoute permission="ver_analitica_comportamiento">
                  <ClientModuleRoute moduleKey="analitica_comportamiento">
                    <AnaliticaComportamiento />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route
              path="/consumo-energia"
              element={
                <PermissionRoute permission="ver_consumo_energia">
                  <ClientModuleRoute moduleKey="consumo_energia">
                    <ConsumoEnergia />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route
              path="/estimaciones-comerciales"
              element={
                <PermissionRoute permission="ver_estimaciones_comerciales">
                  <ClientModuleRoute moduleKey="estimaciones_comerciales">
                    <EstimacionesComerciales />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route path="/conteo-personas"         element={<PermissionRoute permission="ver_conteo_personas"><ConteoPersonas /></PermissionRoute>} />
            <Route path="/temperatura"             element={<PermissionRoute permission="ver_temperatura"><Temperatura /></PermissionRoute>} />
            <Route
              path="/higiene-cumplimiento"
              element={
                <PermissionRoute permission="ver_higiene_cumplimiento">
                  <ClientModuleRoute moduleKey="higiene_cumplimiento">
                    <HigieneCumplimiento />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route
              path="/citas-medicas"
              element={
                <PermissionRoute permission="ver_duracion_estancia">
                  <ClientModuleRoute moduleKey="citas_medicas">
                    <CitasMedicas />
                  </ClientModuleRoute>
                </PermissionRoute>
              }
            />
            <Route path="/seguridad-incidencias"   element={<SuperAdminRoute><SeguridadIncidencias /></SuperAdminRoute>} />
            <Route path="/mapas-calor"             element={<PermissionRoute permission="ver_mapas_calor"><MapasCalor /></PermissionRoute>} />
            <Route path="/mapas-calor/:id"         element={<PermissionRoute permission="ver_mapas_calor"><MapaDetalle /></PermissionRoute>} />
            <Route path="/incidencias"             element={<PermissionRoute permission="ver_incidencias"><Incidencias /></PermissionRoute>} />
            <Route path="/alertas"                 element={<PermissionRoute permission="ver_alertas"><Alertas /></PermissionRoute>} />
            <Route path="/notificaciones"          element={<Notificaciones />} />
            <Route path="/configuracion/notificaciones" element={<AdminRoute><PoliticaNotificaciones /></AdminRoute>} />
            <Route path="/reglas-incidencias"      element={<SuperAdminRoute><ReglasIncidencias /></SuperAdminRoute>} />
            <Route path="/reportes"                element={<PermissionRoute permission="ver_reportes"><Reportes /></PermissionRoute>} />
            <Route path="/usuarios"                element={<AdminRoute><Usuarios /></AdminRoute>} />
            <Route path="/logs"                    element={<SuperAdminRoute><AuditLogs /></SuperAdminRoute>} />
            <Route path="/configuracion"           element={<SuperAdminRoute><Configuracion /></SuperAdminRoute>} />
            <Route path="/perfil"                  element={<Perfil />} />
            <Route path="/sucursales"              element={<AdminRoute><Sucursales /></AdminRoute>} />
            <Route path="/regiones"                 element={<AdminRoute><Regiones /></AdminRoute>} />
            <Route path="/sucursales/:id"          element={<SucursalDetalle />} />
            <Route path="/empleados"               element={<PermissionRoute permission="ver_empleados"><Empleados /></PermissionRoute>} />
            <Route path="/dispositivos"            element={<SuperAdminRoute><Dispositivos /></SuperAdminRoute>} />
            <Route path="/fuentes"                 element={<SuperAdminRoute><Fuentes /></SuperAdminRoute>} />
            <Route path="/clientes"                element={<SuperAdminRoute><Clientes /></SuperAdminRoute>} />
            <Route path="/clientes/:id"            element={<SuperAdminRoute><ClienteDetalle /></SuperAdminRoute>} />
            <Route path="/cuentas"                 element={<SuperAdminRoute><Cuentas /></SuperAdminRoute>} />
            <Route path="/cuentas/:id"             element={<SuperAdminRoute><CuentaDetalle /></SuperAdminRoute>} />
            <Route path="/catalogo-eventos"        element={<SuperAdminRoute><CatalogoEventos /></SuperAdminRoute>} />
          </Route>
        </Route>

        {/* Error routes */}
        <Route path="/403" element={<Error403 />} />
        <Route path="/500" element={<Error500 />} />
        <Route path="*"    element={<Error404 />} />
      </Routes>
    </Suspense>
  );
}


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
